import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert, Table
} from "reactstrap";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { matchesSearch } from "../../../helpers/search";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const KategoriYonetimi = () => {
  document.title = "Kategori Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ─── DATA STATES ──────────────────────────────────────────────
  const [kategoriler, setKategoriler] = useState([]);
  const [baglantiler, setBaglantiler] = useState([]);
  const [ilanSayilari, setIlanSayilari] = useState({});   // { kategoriId: count }
  const [teklifSayilari, setTeklifSayilari] = useState({}); // { kategoriId: count }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── UI STATES ────────────────────────────────────────────────
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'ana' | 'alt'
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // ─── ADD/EDIT MODAL ───────────────────────────────────────────
  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    AD: "", ACIKLAMA: "", SIRA_NO: "", MEZAT_ACIK_MI: false, SADECE_ILAN_MI: true,
    IsPremium: false, Degerle_IsCheck: false, ProhibitedSale: false,
  });
  const [secilenUstKategoriId, setSecilenUstKategoriId] = useState("");
  // Düzenleme açılırken seçili olan üst kategori. Taşımada YALNIZ bu bağlantı
  // kaldırılır; ortak (birden çok üste bağlı) kategorinin öteki bağları korunur.
  const [orijinalUstId, setOrijinalUstId] = useState("");
  // Silme penceresi: { kategori, ustId } — ustId: hangi dalın altından silindiği.
  const [silHedef, setSilHedef] = useState(null);
  const [silIsleniyor, setSilIsleniyor] = useState(false);

  // ─── VIEW LISTINGS MODAL ──────────────────────────────────────
  const [ilanModal, setIlanModal] = useState(false);
  const [seciliKategori, setSeciliKategori] = useState(null);
  const [kategoriIlanlari, setKategoriIlanlari] = useState([]);
  const [isLoadingIlanlar, setIsLoadingIlanlar] = useState(false);

  // ─── BAGLANTI MODAL ───────────────────────────────────────────
  const [baglantiModal, setBaglantiModal] = useState(false);
  const [baglantiKategori, setBaglantiKategori] = useState(null);
  const [yeniBaglantiId, setYeniBaglantiId] = useState("");
  const [isSavingBaglanti, setIsSavingBaglanti] = useState(false);

  // ─── ARAMA STATE'LERİ ─────────────────────────────────────────
  const [ustKatSearch, setUstKatSearch] = useState("");
  const [showUstKatDropdown, setShowUstKatDropdown] = useState(false);
  const [baglantiSearch, setBaglantiSearch] = useState("");
  const [showBaglantiDropdown, setShowBaglantiDropdown] = useState(false);

  // ─── FETCH ALL DATA ───────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [kRes, bRes, ilanRes, teklifRes] = await Promise.all([
        fetch(`${BASE_URL}/KATEGORILER?$orderby=SIRA_NO asc,ID asc`, { headers }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, { headers }),
        fetch(`${BASE_URL}/ILANLAR?$select=ID,KATEGORI_ID&$top=5000`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,ILAN_ID&$top=5000`, { headers }),
      ]);

      const [kData, bData, ilanData, teklifData] = await Promise.all([
        kRes.json(), bRes.json(), ilanRes.json(), teklifRes.json(),
      ]);

      const kats = kData.value || [];
      const bags = bData.value || [];
      const ilanlar = ilanData.value || [];
      const teklifler = teklifData.value || [];

      const isDeletedListing = (i) => (i.AKTIF_DURUM || "").toLowerCase() === "silindi";

      // İlan sayıları: { kategoriId → count } — silinenler hariç
      const ilanCounts = {};
      ilanlar.forEach(i => {
        if (i.KATEGORI_ID && !isDeletedListing(i)) ilanCounts[i.KATEGORI_ID] = (ilanCounts[i.KATEGORI_ID] || 0) + 1;
      });

      // Teklif sayıları: ilan → kategori map üzerinden — silinen ilanların teklifleri sayılmaz
      const ilanKategoriMap = {};
      ilanlar.forEach(i => { if (!isDeletedListing(i)) ilanKategoriMap[i.ID] = i.KATEGORI_ID; });
      const teklifCounts = {};
      teklifler.forEach(t => {
        const katId = ilanKategoriMap[t.ILAN_ID];
        if (katId) teklifCounts[katId] = (teklifCounts[katId] || 0) + 1;
      });

      setKategoriler(kats);
      setBaglantiler(bags);
      setIlanSayilari(ilanCounts);
      setTeklifSayilari(teklifCounts);
    } catch {
      setError("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── COMPUTED VALUES ──────────────────────────────────────────
  const altKategoriIdSet = useMemo(
    () => new Set(baglantiler.map(b => b.ALT_KATEGORI_ID)),
    [baglantiler]
  );

  const kategoriMap = useMemo(() => {
    const m = {};
    kategoriler.forEach(k => { m[k.ID] = k; });
    return m;
  }, [kategoriler]);

  // Ana kategori ID → alt kategori ID listesi
  const altByParent = useMemo(() => {
    const m = {};
    baglantiler.forEach(b => {
      if (!m[b.UST_KATEGORI_ID]) m[b.UST_KATEGORI_ID] = [];
      m[b.UST_KATEGORI_ID].push({ baglantiId: b.ID, altId: b.ALT_KATEGORI_ID });
    });
    return m;
  }, [baglantiler]);

  // Alt kategori ID → üst kategori ID listesi
  const parentsByAlt = useMemo(() => {
    const m = {};
    baglantiler.forEach(b => {
      if (!m[b.ALT_KATEGORI_ID]) m[b.ALT_KATEGORI_ID] = [];
      m[b.ALT_KATEGORI_ID].push(b.UST_KATEGORI_ID);
    });
    return m;
  }, [baglantiler]);

  const stats = useMemo(() => {
    const anaCount = kategoriler.filter(k => !altKategoriIdSet.has(k.ID)).length;
    const altCount = altKategoriIdSet.size;
    const totalIlan = Object.values(ilanSayilari).reduce((a, b) => a + b, 0);
    const totalTeklif = Object.values(teklifSayilari).reduce((a, b) => a + b, 0);
    return { total: kategoriler.length, ana: anaCount, alt: altCount, totalIlan, totalTeklif };
  }, [kategoriler, altKategoriIdSet, ilanSayilari, teklifSayilari]);

  const anaKategoriler = useMemo(
    () => kategoriler.filter(k => !altKategoriIdSet.has(k.ID)),
    [kategoriler, altKategoriIdSet]
  );

  // ─── FILE UPLOAD ──────────────────────────────────────────────
  const handleAcceptedFiles = (files) => {
    const file = files[0];
    Object.assign(file, { preview: URL.createObjectURL(file) });
    setSelectedFile(file);
    setImagePreview(file.preview);
  };

  // ─── MODAL HELPERS ────────────────────────────────────────────
  const resetModal = () => {
    setFormData({ AD: "", ACIKLAMA: "", SIRA_NO: "", MEZAT_ACIK_MI: false, SADECE_ILAN_MI: true, IsPremium: false, Degerle_IsCheck: false, ProhibitedSale: false });
    setIsEditing(false);
    setSelectedFile(null);
    setImagePreview("");
    setSecilenUstKategoriId("");
    setUstKatSearch("");
    setShowUstKatDropdown(false);
  };

  const toggleModal = () => {
    if (modal) resetModal();
    setModal(v => !v);
  };

  const handleEdit = (k) => {
    setFormData({
      ID: k.ID, AD: k.AD || "", ACIKLAMA: k.ACIKLAMA || "", SIRA_NO: k.SIRA_NO ?? "",
      MEZAT_ACIK_MI: !!k.MEZAT_ACIK_MI, SADECE_ILAN_MI: !!k.SADECE_ILAN_MI,
      IsPremium: !!k.IsPremium, Degerle_IsCheck: !!k.Degerle_IsCheck,
      ProhibitedSale: !!k.ProhibitedSale,
      KATEGORILER_IMAGE: k.KATEGORILER_IMAGE || "",
    });
    setImagePreview(k.KATEGORILER_IMAGE || "");
    const parents = parentsByAlt[k.ID];
    setSecilenUstKategoriId(parents?.[0]?.toString() || "");
    setOrijinalUstId(parents?.[0]?.toString() || "");
    setIsEditing(true);
    setModal(true);
  };

  // id'nin tüm alt (ve alt-alt…) kategori ID'leri — taşımada döngü kontrolü için.
  const altSoyKume = (id) => {
    const kume = new Set();
    const yigin = [id];
    while (yigin.length) {
      const cur = yigin.pop();
      (altByParent[cur] || []).forEach(({ altId }) => {
        if (!kume.has(altId)) { kume.add(altId); yigin.push(altId); }
      });
    }
    return kume;
  };

  const handleSave = async () => {
    if (!formData.AD.trim()) { setError("Kategori adı zorunludur."); return; }
    // Taşıma koruması: kategori kendi altına ya da kendi alt dalına taşınamaz
    // (ağaçta döngü oluşur; mobilde kategori zinciri sonsuz döner).
    if (isEditing && secilenUstKategoriId) {
      const ustId = parseInt(secilenUstKategoriId);
      if (ustId === formData.ID || altSoyKume(formData.ID).has(ustId)) {
        setError("Kategori kendisinin ya da kendi alt kategorisinin altına taşınamaz.");
        return;
      }
    }
    setIsSaving(true);
    try {
      let imagePath = formData.KATEGORILER_IMAGE || null;

      if (selectedFile) {
        const fileData = new FormData();
        fileData.append("file", selectedFile);
        const uploadRes = await fetch(
          "https://harmanageladmin.stechomeyazilim.info/Uploads/upload.php",
          { method: "POST", body: fileData }
        );
        const uploadResult = await uploadRes.json();
        if (uploadResult.status) {
          imagePath = "https://harmanageladmin.stechomeyazilim.info/Uploads/" + uploadResult.file_name;
        }
      }

      const payload = {
        AD: formData.AD,
        ACIKLAMA: formData.ACIKLAMA?.trim() || null,
        SIRA_NO: formData.SIRA_NO !== "" && formData.SIRA_NO !== null && formData.SIRA_NO !== undefined
          ? parseInt(formData.SIRA_NO)
          : null,
        MEZAT_ACIK_MI: formData.MEZAT_ACIK_MI,
        SADECE_ILAN_MI: formData.SADECE_ILAN_MI,
        IsPremium: formData.IsPremium,
        Degerle_IsCheck: formData.Degerle_IsCheck,
        ProhibitedSale: formData.ProhibitedSale,
        KATEGORILER_IMAGE: imagePath,
      };

      const url = isEditing
        ? `${BASE_URL}/KATEGORILER(${formData.ID})`
        : `${BASE_URL}/KATEGORILER`;

      const res = await fetch(url, {
        method: isEditing ? "PATCH" : "POST",
        headers, body: JSON.stringify(payload),
      });

      if (res.ok) {
        let savedId = formData.ID;
        if (!isEditing) {
          try { const saved = await res.json(); savedId = saved.ID || saved.id || saved.value?.[0]?.ID; }
          catch { /* PATCH returns 204 */ }
        }

        // SADECE_ILAN_MI cascade: tüm alt ve alt-altı kategorilere özyinelemeli yay
        if (isEditing && formData.ID) {
          const getAllDescendants = (id) => {
            const children = altByParent[id] || [];
            const result = [];
            children.forEach(({ altId }) => {
              result.push(altId);
              result.push(...getAllDescendants(altId));
            });
            return result;
          };
          const allDescendants = getAllDescendants(formData.ID);
          if (allDescendants.length > 0) {
            await Promise.all(allDescendants.map(altId =>
              fetch(`${BASE_URL}/KATEGORILER(${altId})`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({ SADECE_ILAN_MI: payload.SADECE_ILAN_MI }),
              })
            ));
          }
        }

        // Üst kategori bağlantısı ekle/güncelle
        if (secilenUstKategoriId && savedId) {
          const ustId = parseInt(secilenUstKategoriId);
          const existingBag = baglantiler.find(
            b => b.ALT_KATEGORI_ID === savedId && b.UST_KATEGORI_ID === ustId
          );
          if (!existingBag) {
            await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, {
              method: "POST", headers,
              body: JSON.stringify({ UST_KATEGORI_ID: ustId, ALT_KATEGORI_ID: savedId }),
            });
          }
          // TAŞIMA: üst kategori DEĞİŞTİYSE yalnız eski (düzenleme açılırken seçili
          // olan) bağlantıyı sil. Eskiden hiç silinmiyordu → kategori iki yerde
          // kalıyordu. Tüm diğer bağları silmek de yanlış: ortak kategori (ör.
          // traktör + biçer yedek parça) öteki dalından da kopardı.
          const eskiUst = isEditing && orijinalUstId ? parseInt(orijinalUstId) : null;
          const eskiUstler = eskiUst && eskiUst !== ustId
            ? baglantiler.filter(b => b.ALT_KATEGORI_ID === savedId && b.UST_KATEGORI_ID === eskiUst)
            : [];
          await Promise.all(eskiUstler.map(b =>
            fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${b.ID})`, { method: "DELETE", headers })
          ));
        } else if (!secilenUstKategoriId && isEditing && formData.ID) {
          // Üst kategori kaldırıldıysa eski bağlantıları sil
          const eskiBaglar = baglantiler.filter(b => b.ALT_KATEGORI_ID === formData.ID);
          await Promise.all(eskiBaglar.map(b =>
            fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${b.ID})`, { method: "DELETE", headers })
          ));
        }

        setSuccessMsg(isEditing ? "Kategori güncellendi." : "Kategori eklendi.");
        toggleModal();
        setTimeout(() => { fetchAll(); setSuccessMsg(""); }, 400);
      } else {
        setError("İşlem başarısız: " + res.status);
      }
    } catch {
      setError("Bir hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  // Ortak kategori (birden çok üst kategoriye bağlı) silinirken kayıt TAMAMEN
  // silinirse tüm dallardan kaybolur ("birinden silince diğeri de silindi").
  // Bu yüzden önce seçenek sunulur: yalnız bu daldan kaldır / tamamen sil.
  const handleDelete = (k, ustId = null) => {
    const ustler = parentsByAlt[k.ID] || [];
    if (ustler.length > 1) {
      setSilHedef({ kategori: k, ustId: ustId ?? null });
      return;
    }
    if (!window.confirm(`"${k.AD}" kategorisini silmek istediğinize emin misiniz?`)) return;
    kategoriyiTamamenSil(k.ID);
  };

  const kategoriyiTamamenSil = async (id) => {
    setSilIsleniyor(true);
    try {
      await fetch(`${BASE_URL}/KATEGORILER(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Kategori silindi.");
      setSilHedef(null);
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    } finally {
      setSilIsleniyor(false);
    }
  };

  // Yalnız bir üst kategoriyle bağlantıyı koparır; kategori ve ilanları
  // diğer üst kategorilerde yaşamaya devam eder.
  const daldanKaldir = async (altId, ustId) => {
    const bag = baglantiler.find(b => b.ALT_KATEGORI_ID === altId && b.UST_KATEGORI_ID === ustId);
    if (!bag) { setError("Bağlantı bulunamadı."); return; }
    setSilIsleniyor(true);
    try {
      await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${bag.ID})`, { method: "DELETE", headers });
      setSuccessMsg(`Kategori "${kategoriMap[ustId]?.AD || ustId}" altından kaldırıldı; diğer dallarda duruyor.`);
      setSilHedef(null);
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 3500);
    } catch {
      setError("Bağlantı kaldırılamadı.");
    } finally {
      setSilIsleniyor(false);
    }
  };

  // ─── VIEW LISTINGS ────────────────────────────────────────────
  const viewIlanlar = async (k) => {
    setSeciliKategori(k);
    setIlanModal(true);
    setIsLoadingIlanlar(true);
    setKategoriIlanlari([]);
    try {
      const res = await fetch(
        `${BASE_URL}/ILANLAR?$filter=KATEGORI_ID eq ${k.ID}&$expand=KULLANICILAR&$orderby=ID desc&$top=50`,
        { headers }
      );
      const data = await res.json();
      if (data.value) setKategoriIlanlari(data.value);
    } catch { }
    finally { setIsLoadingIlanlar(false); }
  };

  // ─── BAGLANTI MANAGEMENT ──────────────────────────────────────
  const openBaglantiModal = (k) => {
    setBaglantiKategori(k);
    setYeniBaglantiId("");
    setBaglantiSearch("");
    setShowBaglantiDropdown(false);
    setBaglantiModal(true);
  };

  const handleAddBaglanti = async () => {
    if (!yeniBaglantiId || !baglantiKategori) return;
    setIsSavingBaglanti(true);
    try {
      await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, {
        method: "POST", headers,
        body: JSON.stringify({
          UST_KATEGORI_ID: baglantiKategori.ID,
          ALT_KATEGORI_ID: parseInt(yeniBaglantiId),
        }),
      });
      setSuccessMsg("Alt kategori bağlandı.");
      setYeniBaglantiId("");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2000);
    } catch {
      setError("Bağlantı eklenemedi.");
    } finally {
      setIsSavingBaglanti(false);
    }
  };

  const handleDeleteBaglanti = async (baglantiId) => {
    if (!window.confirm("Bu bağlantıyı kaldırmak istiyor musunuz?")) return;
    try {
      await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${baglantiId})`, {
        method: "DELETE", headers,
      });
      fetchAll();
    } catch {
      setError("Bağlantı kaldırılamadı.");
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── BAGlantı modal için listeler ────────────────────────────
  const mevcutAltKategoriler = baglantiKategori
    ? baglantiler
        .filter(b => b.UST_KATEGORI_ID === baglantiKategori.ID)
        .map(b => ({ ...b, altKategori: kategoriMap[b.ALT_KATEGORI_ID] }))
        .filter(b => b.altKategori)
    : [];

  const eklenebilirKategoriler = baglantiKategori
    ? kategoriler.filter(
        k =>
          k.ID !== baglantiKategori.ID &&
          !mevcutAltKategoriler.some(m => m.ALT_KATEGORI_ID === k.ID)
      )
    : [];

  // ─── ROW RENDERER ─────────────────────────────────────────────
// ─── ROW RENDERER ─────────────────────────────────────────────
  const renderRow = (k, level = 0, ustId = null) => {
    const isAna = !altKategoriIdSet.has(k.ID);
    const parents = parentsByAlt[k.ID] || [];
    const altChildren = altByParent[k.ID] || [];
    const ilanCount = ilanSayilari[k.ID] || 0;
    const teklifCount = teklifSayilari[k.ID] || 0;
    const isExpanded = expandedIds.has(k.ID);

    return (
      <React.Fragment key={`${k.ID}-${level}`}>
        <tr className={level > 0 ? "table-light" : ""}>
          {/* Kademe arttıkça soldan boşluk (padding) artar */}
          <td style={{ paddingLeft: `${level * 2.5 + 1}rem` }}>
            {level > 0 && <i className="bx bx-subdirectory-right text-muted me-1" />}
            {k.KATEGORILER_IMAGE ? (
              <img
                src={k.KATEGORILER_IMAGE}
                alt={k.AD}
                className="rounded"
                style={{ width: 36, height: 36, objectFit: "cover" }}
              />
            ) : (
              <div
                className="rounded bg-light d-inline-flex align-items-center justify-content-center text-muted border"
                style={{ width: 36, height: 36 }}
              >
                <i className="bx bx-image" />
              </div>
            )}
          </td>

          <td>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold">{k.AD}</span>
              {isAna ? (
                <Badge color="primary" pill className="font-size-10">Ana Kategori</Badge>
              ) : (
                <Badge color="secondary" pill className="font-size-10">
                  {level}. Alt Kategori
                </Badge>
              )}
              {k.IsPremium && (
                <Badge color="warning" pill className="font-size-10">
                  <i className="bx bxs-star" /> Premium
                </Badge>
              )}
            </div>
            {/* Alt kategori → bağlı olduğu üst kategoriler */}
            {!isAna && parents.length > 0 && (
              <div className="mt-1 d-flex flex-wrap gap-1">
                <span className="text-muted font-size-11">
                  {parents.length > 1 ? <Badge color="warning" className="font-size-10 me-1" title="Birden çok üst kategoride ortak kullanılıyor">Ortak</Badge> : null}
                  Bağlı:
                </span>
                {parents.map(pId =>
                  kategoriMap[pId] ? (
                    <span key={pId} className="badge bg-light text-dark border font-size-10">
                      {kategoriMap[pId].AD}
                    </span>
                  ) : null
                )}
              </div>
            )}
            {/* Alt kategorisi varsa genişlet/daralt butonu (Artık sadece isAna değil, hepsinde çıkacak) */}
            {altChildren.length > 0 && (
              <button
                className="btn btn-link btn-sm p-0 mt-1 font-size-11 text-primary"
                onClick={() => toggleExpand(k.ID)}
              >
                <i className={`bx ${isExpanded ? "bx-chevron-up" : "bx-chevron-down"} me-1`} />
                {altChildren.length} alt kategori {isExpanded ? "gizle" : "göster"}
              </button>
            )}
          </td>

          <td className="text-center">
            <span className="fw-medium">{k.SIRA_NO ?? "-"}</span>
          </td>

          <td className="text-center">
            <span
              className={`badge font-size-12 ${ilanCount > 0 ? "bg-primary" : "bg-light text-dark"}`}
              style={{ minWidth: 32 }}
            >
              {ilanCount}
            </span>
          </td>

          <td className="text-center">
            <span
              className={`badge font-size-12 ${teklifCount > 0 ? "bg-success" : "bg-light text-dark"}`}
              style={{ minWidth: 32 }}
            >
              {teklifCount}
            </span>
          </td>

          <td>
            <div className="d-flex flex-wrap gap-1">
              {k.MEZAT_ACIK_MI && <Badge color="success" className="font-size-10">Mezat</Badge>}
              {k.SADECE_ILAN_MI && <Badge color="info" className="font-size-10">Sadece İlan</Badge>}
              {k.Degerle_IsCheck && <Badge color="purple" className="font-size-10" style={{ background: "#7367f0" }}>Değerleme</Badge>}
              {k.ProhibitedSale && <Badge color="danger" className="font-size-10"><i className="bx bx-block me-1" />Satış Yasak</Badge>}
              {!k.MEZAT_ACIK_MI && !k.SADECE_ILAN_MI && !k.IsPremium && !k.Degerle_IsCheck && !k.ProhibitedSale && (
                <Badge color="light" className="text-dark font-size-10">Standart</Badge>
              )}
            </div>
          </td>

          <td>
            <div className="d-flex gap-1">
              <Button
                color="info" size="sm" outline
                title="İlanları Gör"
                onClick={() => viewIlanlar(k)}
              >
                <i className="bx bx-list-ul" />
              </Button>
              {/* Artık her kategori kendi altına alt kategori alabilir */}
              <Button
                color="dark" size="sm" outline
                title="Alt Kategorileri Yönet"
                onClick={() => openBaglantiModal(k)}
              >
                <i className="bx bx-git-branch" />
              </Button>
              <Button
                color="primary" size="sm" outline
                title="Düzenle"
                onClick={() => handleEdit(k)}
              >
                <i className="bx bx-pencil" />
              </Button>
              <Button
                color="danger" size="sm" outline
                title="Sil"
                onClick={() => handleDelete(k, ustId)}
              >
                <i className="bx bx-trash" />
              </Button>
            </div>
          </td>
        </tr>

        {/* Expanded sub-rows - isAna kısıtlaması kaldırıldı ve level dinamik artırılıyor */}
        {isExpanded && altChildren.map(({ altId }) => {
          const altKat = kategoriMap[altId];
          return altKat ? renderRow(altKat, level + 1, k.ID) : null;
        })}
      </React.Fragment>
    );
  };

  // ─── DİNAMİK SEVİYE VE FİLTRE HESAPLAMALARI ───────────────────

  // 1. Her kategorinin ağaçtaki seviyesini buluyoruz (0: Ana, 1: 1. Alt, 2: 2. Alt vb.)
  const categoryLevels = useMemo(() => {
    const levels = {};
    const getLevel = (id, visited = new Set()) => {
      if (visited.has(id)) return 0; // Sonsuz döngü koruması
      visited.add(id);

      const parents = parentsByAlt[id] || [];
      if (parents.length === 0) return 0; // Ebeveyni yoksa Ana kategoridir (Seviye 0)

      // Üst kategorilerin seviyesini bul, en derininin 1 fazlasını al
      return Math.max(...parents.map(pId => getLevel(pId, new Set(visited)))) + 1;
    };

    kategoriler.forEach(k => {
      levels[k.ID] = getLevel(k.ID);
    });
    return levels;
  }, [kategoriler, parentsByAlt]);

  // 2. Filtre butonlarında yazacak sayıları seviyelere göre grupluyoruz
  const levelStats = useMemo(() => {
    const stats = {};
    Object.values(categoryLevels).forEach(level => {
      stats[level] = (stats[level] || 0) + 1;
    });
    return stats;
  }, [categoryLevels]);

  // 3. filteredKategoriler mantığını dinamik seviyelere göre güncelliyoruz
  const filteredKategoriler = useMemo(() => {
    let list = kategoriler;

    if (activeFilter !== "all" && activeFilter.startsWith("level-")) {
      const targetLevel = parseInt(activeFilter.replace("level-", ""));
      list = kategoriler.filter(k => categoryLevels[k.ID] === targetLevel);
    }

    if (searchQuery.trim()) {
      list = list.filter(k => matchesSearch(k, searchQuery, [k.AD, k.ID]));
    }
    return list;
  }, [kategoriler, activeFilter, searchQuery, categoryLevels]);

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Kategori Yönetimi" />

          {successMsg && (
            <Alert color="success" className="border-0" toggle={() => setSuccessMsg("")}>
              <i className="mdi mdi-check-circle me-2" />{successMsg}
            </Alert>
          )}
          {error && (
            <Alert color="danger" className="border-0" toggle={() => setError(null)}>
              <i className="mdi mdi-alert-outline me-2" />{error}
            </Alert>
          )}

          {/* ── İSTATİSTİK KARTI ─────────────────────────────── */}
          <Row className="mb-4">
            {[
              { label: "Toplam Kategori", value: stats.total, icon: "bx-category", color: "primary" },
              { label: "Ana Kategori", value: stats.ana, icon: "bx-folder", color: "info" },
              { label: "Alt Kategori", value: stats.alt, icon: "bx-git-branch", color: "warning" },
              { label: "Toplam İlan", value: stats.totalIlan, icon: "bx-store-alt", color: "success" },
              { label: "Toplam Teklif", value: stats.totalTeklif, icon: "bx-message-square-dots", color: "danger" },
            ].map((s, i) => (
              <Col key={i} xs={6} md={4} xl>
                <Card className="mini-stats-wid mb-3">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1 font-size-13">{s.label}</p>
                        <h4 className="mb-0">
                          {isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                        </h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── ANA TABLO KARTI ───────────────────────────────── */}
          <Card className="shadow-sm">
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                <div>
                  <h4 className="card-title mb-1">Kategori Hiyerarşisi</h4>
                  <p className="text-muted mb-0 font-size-13">
                    Ana ve alt kategorileri, istatistiklerini ve bağlantılarını yönetin.
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Input
                    type="search"
                    placeholder="Kategori ara..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ width: 180 }}
                    bsSize="sm"
                  />
             <div className="btn-group btn-group-sm">
                    <button
                      className={`btn ${activeFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setActiveFilter("all")}
                    >
                      Tümü ({kategoriler.length})
                    </button>

                    {Object.keys(levelStats)
                      .sort((a, b) => a - b) // Seviyeleri sırala (0, 1, 2...)
                      .map(levelStr => {
                        const level = parseInt(levelStr);
                        const label = level === 0 ? "Ana" : `${level}. Alt`;
                        return (
                          <button
                            key={`level-${level}`}
                            className={`btn ${activeFilter === `level-${level}` ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveFilter(`level-${level}`)}
                          >
                            {label} ({levelStats[level]})
                          </button>
                        );
                    })}
                  </div>

                  <Button
                    color="success"
                    size="sm"
                    className="btn-rounded"
                    onClick={() => { resetModal(); setModal(true); }}
                  >
                    <i className="bx bx-plus me-1" /> Yeni Kategori
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Yükleniyor...</p>
                </div>
              ) : filteredKategoriler.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bx bx-category display-4 mb-3 opacity-50" />
                  <h5>Kategori bulunamadı.</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-hover mb-0" bordered>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 56 }}>Görsel</th>
                        <th>Kategori Adı</th>
                        <th className="text-center" style={{ width: 72 }}>Sıra</th>
                        <th className="text-center" style={{ width: 80 }}>
                          <i className="bx bx-store-alt me-1 text-primary" />İlan
                        </th>
                        <th className="text-center" style={{ width: 80 }}>
                          <i className="bx bx-message-square-dots me-1 text-success" />Teklif
                        </th>
                        <th style={{ width: 200 }}>Özellikler</th>
                        <th style={{ width: 160 }}>İşlemler</th>
                      </tr>
                    </thead>
                  <tbody>
                      {activeFilter === "all" && !searchQuery.trim() ? (
                        // Hiyerarşik görünüm: Sadece 0. seviyeleri (Ana) kök olarak bas, altları kendi renderlanır
                        <>
                          {kategoriler
                            .filter(k => categoryLevels[k.ID] === 0)
                            .map(k => renderRow(k, 0))
                          }
                        </>
                      ) : (
                        // Arama yazılıysa (ya da seviye seçiliyse) TÜM seviyelerde düz liste.
                        // Eskiden "Tümü"de arama yalnız 9 ana kategoride yapılıyordu; hiçbir
                        // alt kategori (ör. "Atlar", traktör markaları) aramayla bulunamıyordu.
                        // Spesifik bir seviye seçildiyse (örn: Sadece 2. Altlar) onları düz liste olarak bas
                        // Kendi indentasyon (boşluk) seviyelerini de koruyarak renderlar.
                        filteredKategoriler.map(k => renderRow(k, categoryLevels[k.ID]))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ══ ORTAK KATEGORİ SİLME SEÇENEĞİ ═════════════════════════ */}
      <Modal isOpen={!!silHedef} toggle={() => !silIsleniyor && setSilHedef(null)} centered>
        <ModalHeader toggle={() => !silIsleniyor && setSilHedef(null)} className="bg-light">
          Ortak kategori: {silHedef?.kategori?.AD}
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="mb-3">
            Bu kategori <strong>{(parentsByAlt[silHedef?.kategori?.ID] || []).length}</strong> üst kategoride
            ortak kullanılıyor. Tamamen silinirse hepsinden birden kaybolur.
          </Alert>
          <p className="mb-2 fw-semibold">Yalnız şu dalın altından kaldır:</p>
          <div className="d-flex flex-column gap-2">
            {(parentsByAlt[silHedef?.kategori?.ID] || []).map(pId => (
              <Button
                key={pId}
                color={silHedef?.ustId === pId ? "primary" : "light"}
                className="text-start border"
                disabled={silIsleniyor}
                onClick={() => daldanKaldir(silHedef.kategori.ID, pId)}
              >
                <i className="bx bx-unlink me-1" />
                {kategoriMap[pId]?.AD || `#${pId}`} altından kaldır
                {silHedef?.ustId === pId ? <small className="ms-2">(şu an buradan açtınız)</small> : null}
              </Button>
            ))}
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <Button
            color="danger"
            outline
            disabled={silIsleniyor}
            onClick={() => {
              if (window.confirm(`"${silHedef.kategori.AD}" TÜM üst kategorilerden silinecek. Emin misiniz?`)) {
                kategoriyiTamamenSil(silHedef.kategori.ID);
              }
            }}
          >
            <i className="bx bx-trash me-1" />Tamamen sil (her yerden)
          </Button>
          <Button color="secondary" disabled={silIsleniyor} onClick={() => setSilHedef(null)}>Vazgeç</Button>
        </ModalFooter>
      </Modal>

      {/* ══ KATEGORİ EKLE/DÜZENLE MODAL ══════════════════════════ */}
      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal} className="bg-light">
          {isEditing ? "Kategori Düzenle" : "Yeni Kategori Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            {/* Görsel */}
            <Col md={4}>
              <Label className="fw-semibold">Kategori Görseli</Label>
              <Dropzone onDrop={handleAcceptedFiles}>
                {({ getRootProps, getInputProps }) => (
                  <div
                    className="dropzone d-flex flex-column align-items-center justify-content-center border border-dashed rounded bg-light text-center"
                    style={{ minHeight: 180, cursor: "pointer" }}
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Önizleme" className="img-thumbnail mb-2" style={{ maxHeight: 130 }} />
                        <span className="text-primary fw-medium font-size-12">
                          <i className="bx bx-edit me-1" />Değiştir
                        </span>
                      </>
                    ) : (
                      <>
                        <i className="display-4 text-muted bx bxs-cloud-upload" />
                        <p className="mt-2 mb-0 font-size-13">Sürükle veya tıkla</p>
                      </>
                    )}
                  </div>
                )}
              </Dropzone>
            </Col>

            {/* Form alanları */}
            <Col md={8}>
              <Row>
                <Col md={8}>
                  <FormGroup className="mb-3">
                    <Label className="fw-semibold">
                      Kategori Adı <span className="text-danger">*</span>
                    </Label>
                    <Input
                      value={formData.AD}
                      onChange={e => setFormData({ ...formData, AD: e.target.value })}
                      placeholder="Örn: Tarım Makineleri"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-3">
                    <Label className="fw-semibold">Sıra No</Label>
                    <Input
                      type="number"
                      value={formData.SIRA_NO}
                      onChange={e => setFormData({ ...formData, SIRA_NO: e.target.value })}
                      placeholder="1"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Açıklama */}
              <FormGroup className="mb-3">
                <Label className="fw-semibold">
                  <i className="bx bx-text me-1 text-info" />
                  Açıklama
                  <span className="text-muted fw-normal ms-1 font-size-11">(Mobil uygulamada kategori kartında görünür)</span>
                </Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={formData.ACIKLAMA}
                  onChange={e => setFormData({ ...formData, ACIKLAMA: e.target.value })}
                  placeholder="Örn: İkinci el ve sıfır tarım makineleri, traktör ilanları."
                  maxLength={500}
                />
                <small className="text-muted">{(formData.ACIKLAMA || "").length}/500</small>
              </FormGroup>

              {/* Üst Kategori Seçimi */}
              <FormGroup className="mb-3">
                <Label className="fw-semibold">
                  <i className="bx bx-git-branch me-1 text-warning" />
                  Üst Kategori — Alt Kategori Yap / Başka Kategoriye Taşı
                </Label>
                <div style={{ position: "relative" }}>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bx bx-search text-muted" />
                    </span>
                    <Input
                      type="text"
                      placeholder="Üst kategori ara..."
                      value={ustKatSearch}
                      onChange={e => { setUstKatSearch(e.target.value); setShowUstKatDropdown(true); }}
                      onFocus={() => setShowUstKatDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUstKatDropdown(false), 180)}
                      autoComplete="off"
                      className="border-start-0"
                    />
                    {secilenUstKategoriId && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => { setSecilenUstKategoriId(""); setUstKatSearch(""); }}
                        title="Seçimi temizle"
                      >
                        <i className="bx bx-x" />
                      </button>
                    )}
                  </div>

                  {/* Seçili kategori etiketi */}
                  {secilenUstKategoriId && !showUstKatDropdown && (
                    <div className="mt-1 px-2 py-1 bg-warning bg-opacity-10 border border-warning rounded-3 d-flex align-items-center gap-2">
                      <i className="bx bx-git-branch text-warning" />
                      <span className="fw-semibold font-size-13 text-dark">
                        {(() => {
                          const k = kategoriler.find(x => x.ID.toString() === secilenUstKategoriId.toString());
                          return k ? `${altKategoriIdSet.has(k.ID) ? "└ " : ""}${k.AD}` : "";
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Açılır liste */}
                  {showUstKatDropdown && (
                    <div
                      style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050,
                        maxHeight: 200, overflowY: "auto",
                        background: "#fff", border: "1px solid #ced4da",
                        borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
                      }}
                    >
                      {/* "Ana kategori olarak ekle" seçeneği */}
                      <div
                        className={`px-3 py-2 font-size-13 text-muted fst-italic ${!secilenUstKategoriId ? "bg-light fw-semibold text-dark" : ""}`}
                        style={{ cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                        onMouseDown={() => { setSecilenUstKategoriId(""); setUstKatSearch(""); setShowUstKatDropdown(false); }}
                      >
                        — Ana Kategori Olarak Ekle —
                      </div>

                      {kategoriler
                        .filter(k => (!isEditing || k.ID !== formData.ID))
                        .filter(k => {
                          return matchesSearch(k, ustKatSearch, [k.AD, k.ID]);
                        })
                        .map(k => (
                          <div
                            key={k.ID}
                            className={`px-3 py-2 d-flex align-items-center gap-2 font-size-13 ${secilenUstKategoriId?.toString() === k.ID.toString() ? "bg-warning bg-opacity-10 fw-semibold" : ""}`}
                            style={{ cursor: "pointer", borderBottom: "1px solid #f8f8f8" }}
                            onMouseDown={() => { setSecilenUstKategoriId(k.ID); setUstKatSearch(""); setShowUstKatDropdown(false); }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fff8e1"}
                            onMouseLeave={e => e.currentTarget.style.background = secilenUstKategoriId?.toString() === k.ID.toString() ? "" : ""}
                          >
                            {altKategoriIdSet.has(k.ID)
                              ? <><i className="bx bx-subdirectory-right text-muted" /><span>{k.AD}</span><span className="badge bg-secondary ms-1 font-size-10">alt</span></>
                              : <><i className="bx bx-folder text-primary" /><span>{k.AD}</span></>
                            }
                          </div>
                        ))
                      }
                      {kategoriler.filter(k => (!isEditing || k.ID !== formData.ID) && matchesSearch(k, ustKatSearch, [k.AD, k.ID])).length === 0 && (
                        <div className="px-3 py-2 text-muted font-size-13">Kategori bulunamadı.</div>
                      )}
                    </div>
                  )}
                </div>
                <small className="text-muted mt-1 d-block">
                  Boş bırakırsanız ana kategori olarak eklenir.
                </small>
              </FormGroup>

              {/* Özellikler */}
              <div className="bg-light p-3 rounded border">
                <Label className="fw-semibold mb-3 border-bottom pb-2 d-block">
                  Modül İzinleri
                </Label>
                <Row>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.MEZAT_ACIK_MI}
                        onChange={e => setFormData({ ...formData, MEZAT_ACIK_MI: e.target.checked })}
                      />
                      <Label check>Mezat Modülü Açık</Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.SADECE_ILAN_MI}
                        onChange={e => setFormData({ ...formData, SADECE_ILAN_MI: e.target.checked })}
                      />
                      <Label check>Sadece İlan</Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.IsPremium}
                        onChange={e => setFormData({ ...formData, IsPremium: e.target.checked })}
                      />
                      <Label check className="text-warning fw-bold">
                        <i className="bx bxs-star me-1" />Premium
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.Degerle_IsCheck}
                        onChange={e => setFormData({ ...formData, Degerle_IsCheck: e.target.checked })}
                      />
                      <Label check>Değerleme Açık</Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.ProhibitedSale}
                        onChange={e => setFormData({ ...formData, ProhibitedSale: e.target.checked })}
                      />
                      <Label check className="text-danger fw-bold">
                        <i className="bx bx-block me-1" />Satış Yasak
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleModal}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : <i className="bx bx-check-double me-1" />}
            {isEditing ? "Kaydet" : "Ekle"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ══ ALT KATEGORİ BAĞLANTI YÖNETİMİ MODAL ════════════════ */}
      <Modal isOpen={baglantiModal} toggle={() => setBaglantiModal(false)} centered size="lg">
        <ModalHeader toggle={() => setBaglantiModal(false)} className="bg-light">
          <i className="bx bx-git-branch me-2 text-dark" />
          <strong>{baglantiKategori?.AD}</strong> — Alt Kategori Bağlantıları
        </ModalHeader>
        <ModalBody>
          {/* Mevcut bağlantılar */}
          <h6 className="fw-bold mb-3">Bağlı Alt Kategoriler ({mevcutAltKategoriler.length})</h6>
          {mevcutAltKategoriler.length === 0 ? (
            <div className="text-muted text-center py-3 bg-light rounded mb-3">
              Henüz alt kategori bağlı değil.
            </div>
          ) : (
            <div className="table-responsive mb-3">
              <Table className="table table-sm table-hover align-middle mb-0 border">
                <thead className="table-light">
                  <tr>
                    <th>Alt Kategori</th>
                    <th className="text-center">İlan</th>
                    <th className="text-center">Teklif</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {mevcutAltKategoriler.map(m => (
                    <tr key={m.ID}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {m.altKategori.KATEGORILER_IMAGE && (
                            <img
                              src={m.altKategori.KATEGORILER_IMAGE}
                              alt=""
                              style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }}
                            />
                          )}
                          <span className="fw-medium">{m.altKategori.AD}</span>
                          {m.altKategori.IsPremium && (
                            <Badge color="warning" className="font-size-10">Premium</Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary">{ilanSayilari[m.altKategori.ID] || 0}</span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success">{teklifSayilari[m.altKategori.ID] || 0}</span>
                      </td>
                      <td className="text-end">
                        <Button
                          color="danger" size="sm" outline
                          onClick={() => handleDeleteBaglanti(m.ID)}
                          title="Bağlantıyı Kaldır"
                        >
                          <i className="bx bx-unlink" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Yeni bağlantı ekle */}
          <div className="border-top pt-3">
            <h6 className="fw-bold mb-2">Alt Kategori Ekle</h6>
            <div className="d-flex gap-2 align-items-start">
              <div style={{ position: "relative", flex: 1 }}>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="bx bx-search text-muted" />
                  </span>
                  <Input
                    type="text"
                    placeholder="Kategori ara..."
                    value={baglantiSearch}
                    onChange={e => { setBaglantiSearch(e.target.value); setShowBaglantiDropdown(true); }}
                    onFocus={() => setShowBaglantiDropdown(true)}
                    onBlur={() => setTimeout(() => setShowBaglantiDropdown(false), 180)}
                    autoComplete="off"
                    className="border-start-0"
                  />
                  {yeniBaglantiId && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => { setYeniBaglantiId(""); setBaglantiSearch(""); }}
                    >
                      <i className="bx bx-x" />
                    </button>
                  )}
                </div>

                {yeniBaglantiId && !showBaglantiDropdown && (
                  <div className="mt-1 px-2 py-1 bg-success bg-opacity-10 border border-success rounded-3 d-flex align-items-center gap-2">
                    <i className="bx bx-check text-success" />
                    <span className="fw-semibold font-size-13 text-dark">
                      {(() => {
                        const k = eklenebilirKategoriler.find(x => x.ID.toString() === yeniBaglantiId.toString());
                        return k ? k.AD : "";
                      })()}
                    </span>
                  </div>
                )}

                {showBaglantiDropdown && (
                  <div
                    style={{
                      position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050,
                      maxHeight: 200, overflowY: "auto",
                      background: "#fff", border: "1px solid #ced4da",
                      borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
                    }}
                  >
                    {eklenebilirKategoriler
                      .filter(k => matchesSearch(k, baglantiSearch, [k.AD, k.ID]))
                      .map(k => (
                        <div
                          key={k.ID}
                          className={`px-3 py-2 d-flex align-items-center gap-2 font-size-13 ${yeniBaglantiId?.toString() === k.ID.toString() ? "bg-success bg-opacity-10 fw-semibold" : ""}`}
                          style={{ cursor: "pointer", borderBottom: "1px solid #f8f8f8" }}
                          onMouseDown={() => { setYeniBaglantiId(k.ID); setBaglantiSearch(""); setShowBaglantiDropdown(false); }}
                          onMouseEnter={e => e.currentTarget.style.background = "#e8f5e9"}
                          onMouseLeave={e => e.currentTarget.style.background = yeniBaglantiId?.toString() === k.ID.toString() ? "" : ""}
                        >
                          {altKategoriIdSet.has(k.ID)
                            ? <><i className="bx bx-subdirectory-right text-muted" /><span>{k.AD}</span><span className="badge bg-secondary ms-1 font-size-10">mevcut alt</span></>
                            : <><i className="bx bx-folder text-primary" /><span>{k.AD}</span><span className="badge bg-light text-dark border ms-1 font-size-10">ana</span></>
                          }
                        </div>
                      ))
                    }
                    {eklenebilirKategoriler.filter(k => matchesSearch(k, baglantiSearch, [k.AD, k.ID])).length === 0 && (
                      <div className="px-3 py-2 text-muted font-size-13">Kategori bulunamadı.</div>
                    )}
                  </div>
                )}
              </div>
              <Button
                color="success"
                onClick={handleAddBaglanti}
                disabled={!yeniBaglantiId || isSavingBaglanti}
              >
                {isSavingBaglanti ? <Spinner size="sm" /> : <i className="bx bx-link-alt me-1" />}
                Bağla
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setBaglantiModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* ══ KATEGORI İLANLARI MODAL ═══════════════════════════════ */}
      <Modal isOpen={ilanModal} toggle={() => setIlanModal(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setIlanModal(false)} className="bg-light">
          <span className="text-primary fw-bold">{seciliKategori?.AD}</span>
          {" "} — Kategorideki İlanlar
        </ModalHeader>
        <ModalBody className="bg-light">
          {isLoadingIlanlar ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Yükleniyor...</p>
            </div>
          ) : kategoriIlanlari.length > 0 ? (
            <div className="table-responsive">
              <Table className="table align-middle table-hover bg-white mb-0 shadow-sm rounded">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>İlan Adı</th>
                    <th>Satıcı</th>
                    <th>Fiyat</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {kategoriIlanlari.map((ilan, idx) => (
                    <tr key={idx}>
                      <td><span className="fw-medium text-muted">#{ilan.ID}</span></td>
                      <td className="fw-medium">{ilan.AD || "-"}</td>
                      <td>{ilan.KULLANICILAR?.AD_SOYAD || "Bilinmiyor"}</td>
                      <td className="fw-bold text-primary">
                        {ilan.FIYAT ? `${Number(ilan.FIYAT).toLocaleString("tr-TR")} ₺` : "-"}
                      </td>
                      <td>
                        {ilan.OLUSTURMA_TARIHI
                          ? new Date(ilan.OLUSTURMA_TARIHI).toLocaleDateString("tr-TR")
                          : "-"}
                      </td>
                      <td>
                        <Badge color={ilan.AKTIF_DURUM === "true" || ilan.AKTIF_DURUM === true ? "success" : "danger"}>
                          {ilan.AKTIF_DURUM === "true" || ilan.AKTIF_DURUM === true ? "Yayında" : "Pasif"}
                        </Badge>
                      </td>
                      <td>
                        <Link
                          to={`/job-details/${ilan.ID}`}
                          className="btn btn-sm btn-light border"
                          onClick={() => setIlanModal(false)}
                        >
                          Git
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded shadow-sm text-muted">
              <i className="bx bx-package display-4 mb-3 opacity-50" />
              <h5>Bu kategoride henüz ilan yok.</h5>
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default KategoriYonetimi;
